import * as React from "react";
import PropTypes from "prop-types";
import { useTheme } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  Grid,
  Slide,
  Stack,
  TextField,
  useMediaQuery
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import UserQuery from "../../stateQueries/User";
import AlertPopup from "../AlertPopup";
import EditIcon from "@mui/icons-material/Edit";
import RichTextEditor from "../Editor";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import TextFieldWrapper from "../FormComponents/TextFieldWrapper";
import SelectFieldWrapper from "../FormComponents/SelectFieldWrapper";

function BootstrapDialogTitle(props) {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 2, fontWeight: "bolder" }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
}

BootstrapDialogTitle.propTypes = {
  children: PropTypes.node,
  onClose: PropTypes.func.isRequired
};

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const AddEditArticleModal = ({ article }) => {
  const [open, setOpen] = React.useState(false);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const queryClient = useQueryClient();

  const addArticleQuery = useMutation({
    mutationFn: async (formData) => {
      return await UserQuery.CSEQuery.addArticle(formData);
    },
    onSuccess: (data) => {
      setTimeout(() => {
        queryClient.invalidateQueries("articles");
        setOpen(false);
      }, 2000);
    },
    onError: (err) => {
      console.log(err);
    }
  });

  const editArticleQuery = useMutation({
    mutationFn: async (formData) => {
      return await UserQuery.CSEQuery.editArticle(formData);
    },
    onSuccess: (data) => {
      setTimeout(() => {
        queryClient.invalidateQueries("articles");
        setOpen(false);
      }, 2000);
    },
    onError: (err) => {
      console.log(err);
    }
  });

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <div>
      {article ? (
        <IconButton color="secondary" onClick={() => setOpen(true)}>
          <EditIcon />
        </IconButton>
      ) : (
        <Button variant="outlined" onClick={() => setOpen(true)}>
          Add Article
        </Button>
      )}

      {addArticleQuery?.isSuccess && (
        <AlertPopup open={true} message={addArticleQuery?.data?.message} />
      )}
      {addArticleQuery?.isError && (
        <AlertPopup
          open={true}
          severity="error"
          message={
            addArticleQuery.error?.response?.data?.message || "Server Error"
          }
        />
      )}

      {editArticleQuery?.isSuccess && (
        <AlertPopup open={true} message={editArticleQuery?.data?.message} />
      )}
      {editArticleQuery?.isError && (
        <AlertPopup
          open={true}
          severity="error"
          message={
            editArticleQuery.error?.response?.data?.message || "Server Error"
          }
        />
      )}

      <Dialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
        fullScreen={fullScreen}
        TransitionComponent={Transition}
        fullWidth
      >
        <BootstrapDialogTitle
          id="customized-dialog-title"
          onClose={handleClose}
        >
          {article ? "Edit News Letter" : "Add News Letter"}
        </BootstrapDialogTitle>
        <DialogContent dividers>
          <Formik
            initialValues={{
              articleId: article?.id || "",
              title: article?.title || "",
              subTitle: article?.subTitle || "",
              author: article?.author || "",
              company: article?.company || "",
              imageUrl: article?.imageUrl || null,
              newsLetterDocument: article?.newsLetterDocument || null,
              content: article?.content || "",
              readTimeValue: article?.readTimeValue || "",
              readTimeUnit: article?.readTimeUnit || "",
              shares: article?.shares || "",
              tags: article?.tags || [],
              status: article?.status || "draft",
              featured: article?.featured || false
            }}
            validationSchema={Yup.object().shape({
              title: Yup.string().required("Title is required"),
              subTitle: Yup.string().required("Sub Title is required"),
              author: Yup.string().required("Author is required"),
              company: Yup.string().required(
                "Company / Department is required"
              ),
              imageUrl: article
                ? Yup.mixed().notRequired()
                : Yup.mixed().required("Image is required"),
              newsLetterDocument: article
                ? Yup.mixed().notRequired()
                : Yup.mixed().required("Document is required"),
              content: Yup.string().required("Content is required"),
              readTimeValue: Yup.number()
                .typeError("Read Time Value must be a number")
                .min(0, "Read Time Value cannot be negative"),
              tags: Yup.array()
                .min(1, "Please select at least one tag")
                .required("Tags are required"),
              featured: Yup.boolean().required("Featured status is required"),
              status: Yup.string().required("Status is required")
            })}
            onSubmit={(values) => {
              const formData = new FormData();

              for (const [key, value] of Object.entries(values)) {
                if (key === "tags") formData.append(key, JSON.stringify(value));
                else formData.append(key, value);
              }

              if (article) {
                editArticleQuery.mutate(formData);
              } else {
                addArticleQuery.mutate(formData);
              }
            }}
            enableReinitialize
          >
            {({ values, setFieldValue }) => {
              const tagOptions = [
                { value: "News", label: "News" },
                { value: "Updates", label: "Updates" },
                { value: "Community", label: "Community" },
                { value: "Announcements", label: "Announcements" },
                { value: "Events", label: "Events" },
                { value: "Spotlight", label: "Spotlight" },
                { value: "Highlights", label: "Highlights" },
                { value: "Features", label: "Features" },
                { value: "Insights", label: "Insights" },
                { value: "Technology", label: "Technology" },
                { value: "Programming", label: "Programming" },
                { value: "Web Development", label: "Web Development" },
                { value: "Mobile Development", label: "Mobile Development" },
                { value: "Data Science", label: "Data Science" },
                { value: "Machine Learning", label: "Machine Learning" },
                {
                  value: "Artificial Intelligence",
                  label: "Artificial Intelligence"
                },
                { value: "Cloud Computing", label: "Cloud Computing" },
                { value: "DevOps", label: "DevOps" },
                { value: "Cybersecurity", label: "Cybersecurity" },
                { value: "Article", label: "Article" },
                { value: "Interview", label: "Interview" },
                { value: "Tutorial", label: "Tutorial" },
                { value: "Review", label: "Review" },
                { value: "Opinion", label: "Opinion" },
                { value: "Case Study", label: "Case Study" },
                { value: "Webinar", label: "Webinar" },
                { value: "Podcast", label: "Podcast" },
                { value: "Video", label: "Video" },
                { value: "Innovation", label: "Innovation" },
                { value: "Strategy", label: "Strategy" },
                { value: "Trends", label: "Trends" },
                { value: "Growth", label: "Growth" },
                { value: "Sustainability", label: "Sustainability" },
                { value: "Leadership", label: "Leadership" },
                { value: "Productivity", label: "Productivity" },
                { value: "Tips", label: "Tips" },
                { value: "Best Practices", label: "Best Practices" }
              ];

              return (
                <Form>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={12}>
                      <TextFieldWrapper name="title" label="Title" />
                    </Grid>

                    <Grid item xs={12} md={12}>
                      <TextFieldWrapper
                        name="subTitle"
                        label="Description"
                        multiline
                        rows={4}
                      />
                    </Grid>

                    <Grid item xs={12} md={12}>
                      <TextFieldWrapper name="author" label="Author" />
                    </Grid>

                    <Grid item xs={12} md={12}>
                      <TextFieldWrapper
                        name="company"
                        label="Company / Department"
                      />
                    </Grid>
                    <Grid item xs={12} md={12}>
                      <RichTextEditor
                        content={values.content}
                        setFieldValue={setFieldValue}
                      />
                    </Grid>
                    <Grid item xs={12} md={12}>
                      <Field name="imageUrl">
                        {({ field, form, meta }) => (
                          <TextField
                            type="file"
                            label="Cover Photo"
                            InputLabelProps={{
                              shrink: true
                            }}
                            // inputProps={{
                            //   accept:
                            //     ".doc, .docx, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/pdf,application/vnd.ms-excel"
                            // }}
                            error={meta.touched && meta.error}
                            helperText={
                              meta.touched && meta.error && meta.error
                            }
                            fullWidth
                            onChange={(event) => {
                              form.setFieldValue(
                                field.name,
                                event.currentTarget.files[0]
                              );
                            }}
                          />
                        )}
                      </Field>
                    </Grid>

                    <Grid item xs={12} md={12}>
                      <Field name="newsLetterDocument">
                        {({ field, form, meta }) => (
                          <TextField
                            type="file"
                            label="News Letter Document"
                            InputLabelProps={{
                              shrink: true
                            }}
                            // inputProps={{
                            //   accept:
                            //     ".doc, .docx, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/pdf,application/vnd.ms-excel"
                            // }}
                            error={meta.touched && meta.error}
                            helperText={
                              meta.touched && meta.error && meta.error
                            }
                            fullWidth
                            onChange={(event) => {
                              form.setFieldValue(
                                field.name,
                                event.currentTarget.files[0]
                              );
                            }}
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextFieldWrapper
                        name="readTimeValue"
                        label="Read Time Value"
                        type="number"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <SelectFieldWrapper
                        name="readTimeUnit"
                        label="Read Time Unit"
                        options={[
                          { value: "minutes", label: "Minutes" },
                          { value: "hours", label: "Hours" },
                          { value: "days", label: "Days" }
                        ]}
                      />
                    </Grid>
                    <Grid item xs={12} md={12}>
                      <SelectFieldWrapper
                        name="featured"
                        label="Featured"
                        options={[
                          { value: true, label: "Yes" },
                          { value: false, label: "No" }
                        ]}
                      />
                    </Grid>

                    <Grid item xs={12} md={12}>
                      <FormControl fullWidth>
                        <Autocomplete
                          multiple
                          options={tagOptions}
                          defaultValue={values?.tags ? values?.tags : null}
                          isOptionEqualToValue={(option, value) => {
                            return (
                              JSON.stringify(option) === JSON.stringify(value)
                            );
                          }}
                          onChange={(e, value) => {
                            setFieldValue("tags", value);
                          }}
                          disableCloseOnSelect
                          getOptionLabel={(option) => option?.label}
                          renderOption={(props, option, { selected }) => {
                            return (
                              <li {...props} key={option?.value}>
                                <Checkbox
                                  // icon={icon}
                                  // checkedIcon={checkedIcon}
                                  style={{ marginRight: 8 }}
                                  checked={selected}
                                />
                                {option?.label}
                              </li>
                            );
                          }}
                          renderInput={(params) => (
                            <TextField
                              fullWidth
                              {...params}
                              label="Tags"
                              placeholder="Select Tags"
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={12}>
                      <SelectFieldWrapper
                        name="status"
                        label="Status"
                        options={[
                          { value: "draft", label: "Draft" },
                          { value: "published", label: "Published" }
                        ]}
                      />
                    </Grid>

                    <Grid item xs={12} md={12} mt={0}>
                      <Box textAlign="end">
                        <Stack
                          spacing={2}
                          direction="row"
                          width="100%"
                          justifyContent="end"
                        >
                          <Button
                            variant="contained"
                            color="error"
                            onClick={() => setOpen(false)}
                          >
                            Close
                          </Button>
                          {article ? (
                            <Button variant="contained" type="submit">
                              {editArticleQuery.isLoading ? (
                                <CircularProgress color="secondary" />
                              ) : (
                                "Update"
                              )}
                            </Button>
                          ) : (
                            <Button variant="contained" type="submit">
                              {addArticleQuery.isLoading ? (
                                <CircularProgress color="secondary" />
                              ) : (
                                "Submit"
                              )}
                            </Button>
                          )}
                        </Stack>
                      </Box>
                    </Grid>
                  </Grid>
                </Form>
              );
            }}
          </Formik>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AddEditArticleModal;
